import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileUp, RefreshCw, RotateCcw, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { useDemoStore } from '@/state/store';
import { clearDemoStorage } from '@/state/storage';
import { can, roleLabel } from '@/domain/roles';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort, formatDateTime, formatRange } from '@/domain/time';
import { machineLabel, presentText } from '@/lib/present';
import { DEMO_NOW_UTC, DEMO_WINDOW, SEED_VERSION } from '@/fixtures/scenarios';
import { FIELD_LABELS } from '@/lib/labels';
import { ADAPTER_OPTIONS } from '@/adapters';
import { CSV_MAX_BYTES, CSV_MAX_ROWS, parseMachineCsv, REQUIRED_COLUMNS } from '@/adapters/csvImport';
import { simulatedSync } from '@/adapters/apiAdapter';
import { MACHINES } from '@/fixtures/generator';
import { ErrorState, NoAccess, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { CoverageBadge, Note, SourceStatusBadge } from '@/components/app/Badges';
import { PageHeader, DefList } from '@/components/app/PageHeader';
import { useRecordViewer } from '@/components/app/RecordViewer';
import { ImportContractPanel } from '@/components/app/ImportContractPanel';
import { cn } from '@/lib/utils';

function SourcePanel({ source, coverage, onSync, syncing }) {
  return (
    <Panel data-testid={`source-panel-${source.sourceId}`} className={cn(source.status === 'error' && 'border-[rgba(180,35,24,0.4)]')}>
      <PanelHeader
        title={source.name}
        description={source.description}
        actions={
          <>
            <SourceStatusBadge status={source.status} />
            {!source.isContextOnly && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => onSync(source.sourceId)} disabled={syncing === source.sourceId} data-testid={`simulated-sync-button-${source.sourceId}`}>
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', syncing === source.sourceId && 'animate-spin')} aria-hidden="true" />
                {source.status === 'error' ? 'Prøv igjen' : 'Simulert synk'}
              </Button>
            )}
          </>
        }
      />
      <div className="space-y-4 px-5 py-4">
        <DefList
          cols={3}
          items={[
            { label: 'Modus', value: source.modeLabel },
            { label: 'Dataperiode', value: source.dataFrom ? `${formatDateShort(source.dataFrom)} – ${formatDateShort(source.dataTo)}` : 'Ingen data' },
            { label: 'Sist observert måling', value: formatDateTime(source.lastObservedAt) },
            { label: 'Sist forsøk', value: formatDateTime(source.attemptedAt) },
            { label: 'Sist vellykket synk', value: formatDateTime(source.succeededAt), className: source.status === 'delayed' ? 'text-[var(--hr-amber)] font-medium' : '' },
            { label: 'Forventet frekvens', value: source.expectedCadenceMinutes ? `hver ${source.expectedCadenceMinutes} min` : '–' },
          ]}
        />
        {source.fields.length > 0 && (
          <div>
            <div className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Datadekning per målefelt (valgt periode)</div>
            <ul className="space-y-1.5 text-sm">
              {source.fields.map((f) => {
                const cov = coverage.find((c) => c.field === f.field);
                return (
                  <li key={f.field} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{FIELD_LABELS[f.field] ?? f.field}</span>
                    <span className="text-[13px] text-[var(--hr-muted)]">({f.unit}) {f.description}</span>
                    {cov ? <CoverageBadge status={cov.status} label={`${cov.received}/${cov.expected}`} /> : <CoverageBadge status="not_applicable" label={source.isContextOnly ? 'kontekst' : 'ikke målt i perioden'} />}
                    {cov && <span className="basis-full text-[12px] leading-4 text-[var(--hr-muted-2)]" data-testid={`coverage-note-${f.field}`}>Teller {cov.countsLabel}{cov.note ? ` · ${cov.note}` : ''}</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <p className="text-[13px] leading-5 text-[var(--hr-muted)]">{presentText(source.note)}</p>
      </div>
    </Panel>
  );
}

function ImportWizard() {
  const { dataset, importRows, setImportRows, importSummary, importHistory, baseDataset } = useData();
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const inputRef = useRef(null);
  const knownIds = MACHINES.map((m) => m.machineId);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    setParsed(null);
    setFileError(null);
    setConfirmed(false);
    if (!f) return;
    setFile(f);
    if (f.size > CSV_MAX_BYTES) {
      setFileError(`Filen er ${fmtNumber(f.size / 1024 / 1024, 2)} MB. Maks 5 MB.`);
      return;
    }
    const text = await f.text();
    setParsed(parseMachineCsv(text, knownIds));
  };

  const apply = () => {
    if (!parsed?.ok || !confirmed) return;
    const existing = importRows ?? [];
    const merged = [...existing.filter((x) => !parsed.rows.some((n) => n.recordId === x.recordId && n.revision === x.revision)), ...parsed.rows];
    setImportRows(merged);
    toast.success('Importen er tatt i bruk for økten', { description: `${parsed.rows.length} rader validert. Grunndatasettet er uendret; importen kan forkastes.` });
    setParsed(null);
    setFile(null);
    setConfirmed(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const discard = () => {
    setImportRows(null);
    toast.message('Importen er forkastet', { description: 'Grunndatasettet er gjenopprettet for økten.' });
  };

  const preview = parsed?.rows.slice(0, 20) ?? [];
  const errorsByRow = useMemo(() => {
    const m = new Map();
    for (const e of parsed?.errors ?? []) m.set(e.row, [...(m.get(e.row) ?? []), e]);
    return m;
  }, [parsed]);

  return (
    <Panel data-testid="csv-import-panel">
      <PanelHeader
        title="Kontrollert CSV-import"
        description={`Normalisert maskinfil etter VisionLink-kontrakten. Maks 5 MB og ${fmtNumber(CSV_MAX_ROWS, 0)} rader. Hele filen avvises ved feil – ingen delvis mutasjon. Idempotent: samme record_id og revisjon gir ingen endring; høyere revisjon erstatter gammelt bidrag. reported_co2_kg prioriteres foran estimat fra diesel for intervallet.`}
        actions={
          <>
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href="/maler/maskindata-mal.csv" download data-testid="download-template-button"><Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Mal</a>
            </Button>
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href="/maler/eksempel-korreksjon-D02.csv" download data-testid="download-example-valid-button"><Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Eksempel (gyldig)</a>
            </Button>
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href="/maler/eksempel-ugyldig.csv" download data-testid="download-example-invalid-button"><Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Eksempel (ugyldig)</a>
            </Button>
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href="/maler/visionlink-eksempel.csv" download data-testid="download-example-visionlink-button"><Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Eksempel (VisionLink m/ CO₂)</a>
            </Button>
          </>
        }
      />
      <div className="space-y-4 px-5 py-4">
        <div data-testid="csv-column-contract">
          <div className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Kolonner i filen</div>
          <ul className="flex flex-wrap gap-1.5">
            {REQUIRED_COLUMNS.map((col) => (
              <li key={col} className="rounded-md border border-[var(--hr-border)] bg-white px-2 py-0.5 font-mono text-[12px] leading-5 text-[var(--hr-heading)]">
                {col}
              </li>
            ))}
            {['revision', 'reported_co2_kg', 'activity', 'source'].map((col) => (
              <li key={col} className="rounded-md border border-dashed border-[var(--hr-border)] px-2 py-0.5 font-mono text-[12px] leading-5 text-[var(--hr-muted)]">
                {col}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[13px] leading-5 text-[var(--hr-muted)]">Hele rammer er påkrevd, stiplede er valgfrie. Skilletegn «;».</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="csv-file" className="text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">1. Velg CSV-fil</Label>
            <Input ref={inputRef} id="csv-file" type="file" accept=".csv,text/csv" onChange={onFile} className="mt-1 bg-white" data-testid="csv-import-upload-input" />
          </div>
        </div>
        {fileError && <Note tone="red" data-testid="csv-file-error">{fileError}</Note>}
        {parsed && (
          <div className="space-y-3" data-testid="csv-import-result">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{file?.name}</span>
              <span className="text-[var(--hr-muted)]">· {parsed.rowCount} rader · skilletegn «{parsed.delimiter}»</span>
              {parsed.ok ? <CoverageBadge status="complete" label="Validert uten feil" /> : <CoverageBadge status="missing" label={`${parsed.headerErrors.length + parsed.errors.length} feil – filen avvises`} />}
            </div>
            {parsed.headerErrors.length > 0 && (
              <Note tone="red" data-testid="csv-header-errors">
                <ul className="list-disc pl-4">
                  {parsed.headerErrors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </Note>
            )}
            {parsed.errors.length > 0 && (
              <div className="overflow-x-auto rounded-[var(--hr-radius-sm)] border border-[rgba(180,35,24,0.3)]">
                <Table data-testid="csv-row-errors-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rad</TableHead>
                      <TableHead>Kolonne</TableHead>
                      <TableHead>Verdi</TableHead>
                      <TableHead>Grunn</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.errors.slice(0, 50).map((e, i) => (
                      <TableRow key={i} data-testid={`csv-row-error-${e.row}`}>
                        <TableCell className="hr-tabular">{e.row}</TableCell>
                        <TableCell className="font-mono text-[13px]">{e.column}</TableCell>
                        <TableCell className="font-mono text-[13px]">{e.value}</TableCell>
                        <TableCell>{e.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {parsed.errors.length > 50 && <p className="px-3 py-2 text-[13px] text-[var(--hr-muted)]">Viser 50 av {parsed.errors.length} feil.</p>}
              </div>
            )}
            {preview.length > 0 && (
              <div>
                <div className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">2. Forhåndsvisning (første {preview.length} rader)</div>
                <div className="max-h-[300px] overflow-auto rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)]">
                  <Table data-testid="csv-import-preview-table">
                    <TableHeader className="sticky top-0 bg-white">
                      <TableRow>
                        <TableHead>Rad</TableHead>
                        <TableHead>record_id</TableHead>
                        <TableHead>machine_id</TableHead>
                        <TableHead>from_utc</TableHead>
                        <TableHead>to_utc</TableHead>
                        <TableHead className="text-right">engine_hours</TableHead>
                        <TableHead className="text-right">idle_hours</TableHead>
                        <TableHead className="text-right">fuel_litres</TableHead>
                        <TableHead className="text-right">reported_co2_kg</TableHead>
                        <TableHead>quality</TableHead>
                        <TableHead className="text-right">rev.</TableHead>
                        <TableHead>Feil</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.map((row) => {
                        const errs = errorsByRow.get(row.row) ?? [];
                        return (
                          <TableRow key={row.row} className={cn(errs.length && 'bg-[var(--hr-red-bg)]')}>
                            <TableCell className="hr-tabular">{row.row}</TableCell>
                            <TableCell className="font-mono text-[13px]">{row.recordId}</TableCell>
                            <TableCell>{machineLabel(row.machineId)}</TableCell>
                            <TableCell className="font-mono text-[13px]">{row.fromUtc}</TableCell>
                            <TableCell className="font-mono text-[13px]">{row.toUtc}</TableCell>
                            <TableCell className="text-right hr-tabular">{row.engineHours ?? 'null'}</TableCell>
                            <TableCell className="text-right hr-tabular">{row.idleHours ?? 'null'}</TableCell>
                            <TableCell className="text-right hr-tabular">{row.fuelLitres ?? 'null'}</TableCell>
                            <TableCell className="text-right hr-tabular">{row.reportedCo2Kg ?? 'null'}</TableCell>
                            <TableCell>{row.quality}</TableCell>
                            <TableCell className="text-right hr-tabular">{row.revision}</TableCell>
                            <TableCell className="text-[13px] text-[var(--hr-red)]">{errs.map((e) => e.column).join(', ')}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {parsed.ok && (
              <div className="space-y-3 rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)] bg-[var(--hr-bg)] px-4 py-3">
                <div className="text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">3. Bekreft og bruk</div>
                <div className="flex items-start gap-2">
                  <Checkbox id="confirm-fictional" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="csv-confirm-fictional-checkbox" />
                  <Label htmlFor="confirm-fictional" className="text-sm font-normal leading-5">Jeg bekrefter at filen ikke inneholder reelle persondata eller serienumre, og at den er ment for denne prototypen.</Label>
                </div>
                <Button onClick={apply} disabled={!confirmed} data-testid="csv-import-confirm-button">
                  <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" /> Ta i bruk importen
                </Button>
              </div>
            )}
          </div>
        )}
        <div className="border-t border-[var(--hr-border)] pt-4">
          <div className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Aktiv import i denne økten</div>
          {importRows?.length ? (
            <div className="space-y-2 text-sm" data-testid="active-import-summary">
              <p>
                {importRows.length} importerte rader · {importSummary?.added ?? 0} nye, {importSummary?.replaced ?? 0} erstattet (ny revisjon), {importSummary?.unchanged ?? 0} uendret, {importSummary?.ignoredOlderRevision ?? 0} ignorert (eldre revisjon). Grunndatasettet har {baseDataset?.machineIntervals.length} intervaller; øktens datasett har {dataset?.machineIntervals.length}.
              </p>
              {importSummary?.overlapWarnings?.length > 0 && (
                <Note tone="amber">
                  {importSummary.overlapWarnings.slice(0, 3).map((w) => (
                    <div key={w}>{w}</div>
                  ))}
                </Note>
              )}
              {importHistory.length > 0 && <p className="text-[13px] text-[var(--hr-muted)]">{importHistory.length} tidligere revisjon(er) er bevart i historikken (erstattet, ikke slettet).</p>}
              <Button variant="outline" size="sm" onClick={discard} data-testid="discard-import-button">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Forkast importerte data
              </Button>
            </div>
          ) : (
            <p className="text-sm text-[var(--hr-muted)]">Ingen import er aktiv. Skjermene bruker grunndatasettet.</p>
          )}
        </div>
      </div>
    </Panel>
  );
}

function RatesPanel() {
  const { rates, setRates, ratesOverridden } = useData();
  const [form, setForm] = useState({ diesel: String(rates.dieselNokPerLitre), machine: String(rates.machineNokPerHour), co2: String(rates.co2KgPerLitre) });
  useEffect(() => setForm({ diesel: String(rates.dieselNokPerLitre), machine: String(rates.machineNokPerHour), co2: String(rates.co2KgPerLitre) }), [rates]);
  const num = (s) => Number(String(s).replace(',', '.'));
  const valid = [form.diesel, form.machine, form.co2].every((s) => Number.isFinite(num(s)) && num(s) >= 0);
  const save = () => {
    if (!valid) return;
    setRates({ dieselNokPerLitre: num(form.diesel), machineNokPerHour: num(form.machine), co2KgPerLitre: num(form.co2) });
    toast.success('Satser oppdatert', { description: 'Alle kost- og CO₂-tall beregnes på nytt fra grunnlagsdata.' });
  };
  return (
    <Panel data-testid="rates-panel">
      <PanelHeader title="Antatte satser" description="Antatte satser, ikke kundens avtalte satser. Konfigurerbare; påvirker illustrativ kostnad og estimert CO₂ i alle skjermer." actions={ratesOverridden && <CoverageBadge status="partial" label="Endret fra standard" />} />
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-3">
        <div>
          <Label htmlFor="rate-diesel" className="text-[13px]">Diesel (NOK/L)</Label>
          <Input id="rate-diesel" inputMode="decimal" value={form.diesel} onChange={(e) => setForm({ ...form, diesel: e.target.value })} className="mt-1 bg-white hr-tabular" data-testid="rate-diesel-input" />
        </div>
        <div>
          <Label htmlFor="rate-machine" className="text-[13px]">Maskintime uten drivstoff (NOK/h)</Label>
          <Input id="rate-machine" inputMode="decimal" value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} className="mt-1 bg-white hr-tabular" data-testid="rate-machine-input" />
        </div>
        <div>
          <Label htmlFor="rate-co2" className="text-[13px]">Utslippsfaktor (kg CO₂/L)</Label>
          <Input id="rate-co2" inputMode="decimal" value={form.co2} onChange={(e) => setForm({ ...form, co2: e.target.value })} className="mt-1 bg-white hr-tabular" data-testid="rate-co2-input" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-5 pb-4">
        <Button size="sm" onClick={save} disabled={!valid} data-testid="rates-save-button">Lagre satser</Button>
        <Button size="sm" variant="outline" onClick={() => setRates(null)} disabled={!ratesOverridden} data-testid="rates-reset-button">Tilbake til standard (20 / 400 / 2,68)</Button>
        {!valid && <span className="text-[13px] text-[var(--hr-red)]">Alle satser må være tall ≥ 0.</span>}
        <span className="text-[13px] text-[var(--hr-muted)]">Gyldig fra {formatDateShort(rates.effectiveFrom)} · {rates.provenance}. Produksjonsbruk krever verifisert faktor, kilde, gyldighetsdato og avgrensning.</span>
      </div>
    </Panel>
  );
}

export default function DatakilderPage() {
  const r = useResults();
  const { role } = useSession();
  const data = useData();
  const store = useDemoStore();
  const { openRecord } = useRecordViewer();
  const [syncing, setSyncing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  const post = r.sp.get('post');
  useEffect(() => {
    if (post && data.status === 'ready') {
      openRecord(post);
      r.set({ post: null }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, data.status]);

  if (!can(role, 'view_sources')) {
    return (
      <div className="space-y-6">
        <PageHeader title="Datakilder og integrasjoner" />
        <NoAccess roleLabel={roleLabel(role)} what="kildeinnsyn og integrasjonsoppsett" />
      </div>
    );
  }

  const onSync = async (sourceId) => {
    setSyncing(sourceId);
    const attemptedAt = new Date().toISOString();
    try {
      let result;
      if (data.adapterKind === 'api') {
        result = await simulatedSync(sourceId);
      } else {
        await new Promise((res) => setTimeout(res, 700));
        result = { status: 'updated', attemptedAt, succeededAt: attemptedAt, message: 'Simulert synk fullført lokalt. Ingen reell kobling.' };
      }
      data.recordSync(sourceId, { attemptedAt: result.attemptedAt, succeededAt: result.succeededAt, status: result.status === 'error' ? 'error' : 'updated', note: result.message });
      toast.success('Simulert synk fullført', { description: 'Dette er en fiktiv synk uten reell kobling til kundens systemer. Grunnlagsdata er uendret.' });
    } catch (e) {
      data.recordSync(sourceId, { attemptedAt, status: 'error', note: e.message });
      toast.error('Simulert synk feilet', { description: e.message });
    } finally {
      setSyncing(null);
    }
  };

  const onReset = () => {
    clearDemoStorage({ includeSession: false });
    store.clear();
    data.resetLocalData();
    setResetOpen(false);
    toast.success('Portaldata er nullstilt', { description: 'Kun portalens egne lagrede handlinger ble fjernet. Grunndatasettet er gjenopprettet.' });
  };

  const coverage = r.ready ? r.current.coverage : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Datakilder" title="Hvor kommer tallene fra, og hvor ferske er de?" lead={`Fire tydelig separate kilder. Produksjon og Maskindata er beregningsgrunnlag; SmartDok og Økonomi er kun kontekst og planlagt kobling. Datadekning gjelder ${r.ready ? formatRange(r.fromDate, r.toDate) : 'valgt periode'}. Referansetidspunkt: ${formatDateTime(DEMO_NOW_UTC)}.`}>
        {can(role, 'reset') && (
          <Button variant="outline" size="sm" className="h-9" onClick={() => setResetOpen(true)} data-testid="reset-demo-open-button-page">
            <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" /> Nullstill portaldata
          </Button>
        )}
      </PageHeader>

      {data.status === 'error' && <ErrorState title="Kunne ikke hente data fra adapteren" source={data.adapterLabel || 'Simulert API'} description={data.error} onRetry={data.retry} />}

      {can(role, 'switch_adapter') && (
        <Panel data-testid="adapter-panel">
          <PanelHeader title="Adapter" description="MockAdapter, CsvAdapter (import) og ApiAdapter implementerer samme lesende kontrakt: getProduction, getMachineIntervals, getAllocations, getCoverage, getSourceStatus. Skjermene trenger ikke omskriving ved bytte." />
          <div className="grid gap-4 px-5 py-4 md:grid-cols-[280px_1fr]">
            <div>
              <Label htmlFor="adapter-select" className="text-[13px]">Aktiv adapter</Label>
              <Select value={data.adapterKind} onValueChange={(v) => data.setAdapterKind(v)}>
                <SelectTrigger id="adapter-select" className="mt-1 bg-white" data-testid="adapter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADAPTER_OPTIONS.map((o) => (
                    <SelectItem key={o.kind} value={o.kind} data-testid={`adapter-option-${o.kind}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[13px] text-[var(--hr-muted)]">{ADAPTER_OPTIONS.find((o) => o.kind === data.adapterKind)?.description}</p>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Switch id="fail-mode" checked={data.failMode} onCheckedChange={data.setFailMode} disabled={data.adapterKind !== 'api'} data-testid="simulate-source-error-switch" />
                <Label htmlFor="fail-mode" className="font-normal">Simuler kildefeil (kun Simulert API) – viser tilstanden «Kildefeil» med «Prøv igjen»</Label>
              </div>
              <p className="text-[13px] leading-5 text-[var(--hr-muted)]">
                Status: {data.status === 'ready' ? 'klar' : data.status === 'loading' ? 'laster' : 'feil'} · {data.adapterLabel}. Alle adaptere er simulerte; ingen leverandørendepunkter er oppfunnet og ingen kundesystemer kontaktes. Portalen fortsetter å virke frakoblet med den lokale adapteren.
              </p>
            </div>
          </div>
        </Panel>
      )}

      {data.status === 'loading' && <PageSkeleton />}
      {data.status === 'ready' && (
        <div className="grid gap-6 lg:grid-cols-2">
          {data.sources.map((s) => (
            <SourcePanel key={s.sourceId} source={s} coverage={coverage} onSync={onSync} syncing={syncing} />
          ))}
        </div>
      )}

      {data.status === 'ready' && (
        <Note tone="info" data-testid="example-data-disclosure">
          <strong>Datagrunnlag: eksempeldata.</strong> Produksjon og Maskindata er et deterministisk eksempeldatasett ({presentText(SEED_VERSION)}) for {formatDateShort(DEMO_WINDOW.from)} – {formatDateShort(DEMO_WINDOW.to)} med fast referansetidspunkt. Ingen kundesystemer er koblet til, og ingen tall er Hardanger Rocks faktiske driftstall. Kvalitetsvarsler, enheter, dekning og usikkerhet vises som de ville gjort med reelle kilder.
        </Note>
      )}

      {r.ready && r.current.warnings.filter((w) => w.recordIds?.length).length > 0 && (
        <Panel data-testid="data-quality-panel">
          <PanelHeader title="Datakvalitet i valgt periode" description="Varsler fra beregningsmotoren med lenke til kildepostene." />
          <ul className="divide-y divide-[var(--hr-border)]">
            {r.current.warnings
              .filter((w) => w.recordIds?.length)
              .map((w) => (
                <li key={w.code + (w.machineId ?? '') + (w.recordIds?.[0] ?? '')} className="flex flex-col gap-1.5 px-5 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <CoverageBadge status={w.severity === 'high' ? 'missing' : 'partial'} label={w.code} />
                    <span>{presentText(w.message)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {w.recordIds.slice(0, 6).map((id) => (
                      <button key={id} type="button" onClick={() => openRecord(id)} className="rounded-md border border-[var(--hr-border)] bg-white px-2 py-0.5 font-mono text-[13px] transition-colors duration-150 hover:border-[var(--hr-accent)] hover:text-[var(--hr-accent-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`quality-record-${id}`}>
                        {presentText(id)}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
          </ul>
        </Panel>
      )}

      {can(role, 'import') ? <ImportWizard /> : <Note>CSV-import er forbeholdt rollen Dataadministrator.</Note>}
      {can(role, 'edit_rates') && <RatesPanel />}

      {can(role, 'view_sources') && data.status === 'ready' && <ImportContractPanel lineIds={(data.dataset?.lines ?? []).map((l) => l.lineId)} fractionIds={(data.dataset?.fractions ?? []).map((f) => f.fractionId)} />}

      <Panel data-testid="contract-panel">
        <PanelHeader title="Kontrakt for senere API" description="Lesende adaptergrensesnitt. Query: siteId, fromUtc, toUtc, scenarioId. Respons: data, coverage, warnings, sourceRefs." />
        <div className="grid gap-4 px-5 py-4 md:grid-cols-2">
          <pre className="overflow-x-auto rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)] bg-[var(--hr-bg)] p-3 text-[13px] leading-5">{`interface DataAdapter {
  getProduction(q): AdapterResponse<ProductionInterval>
  getLineStates(q): AdapterResponse<LineStateInterval>
  getMachineIntervals(q): AdapterResponse<MachineInterval>
  getAllocations(q): AdapterResponse<ActivityAllocation>
  getCoverage(q): FieldCoverage[]
  getSourceStatus(scenarioId): SourceSync[]
}
query = { siteId, fromUtc, toUtc, scenarioId }
response = { data, coverage, warnings, sourceRefs }`}</pre>
          <div className="space-y-2 text-sm leading-6">
            <p>Tidsrom er halvåpne [fromUtc, toUtc). ISO 8601 med Z i lagring; døgn grupperes i Europe/Oslo. Målt 0 er gyldig; manglende verdi er null med status «missing».</p>
            <p>Kildepost identifiseres av sourceId + sourceRecordId + revision. Idempotent import; ny revisjon erstatter gammelt bidrag, historikken beholdes.</p>
            <p>Når to kilder dekker samme observasjon brukes eksplisitt prioritert kilde, ikke summen. Produsert tonn kommer alltid fra produksjonskilden.</p>
            <p className="text-[13px] text-[var(--hr-muted)]">Før pilot: bekreft PLS-eksport/API, telematikkdekning per maskin, felles avgrensning av aktiviteter og hvem som er master for hvert felt. TeamViewer-tilgang er ikke en datakontrakt.</p>
          </div>
        </div>
      </Panel>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-demo-dialog-page">
          <AlertDialogHeader>
            <AlertDialogTitle>Nullstille portaldata?</AlertDialogTitle>
            <AlertDialogDescription>Fjerner kun portalens egne lagrede handlinger (kommentarer, avviksstatus, tiltaksforslag, import, satser, adaptervalg) og gjenoppretter grunndatasettet. Økten beholdes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-demo-cancel-button-page">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={onReset} data-testid="reset-demo-confirm-button-page">Nullstill</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
